import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { CalendarClock, RefreshCw, Package } from "lucide-react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { AddComponent } from "@/components/catalogue/BundleComponentsCard";

const SiteBadge = ({ site }) => <span className={`whitespace-nowrap rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${site === "new" ? "bg-emerald-100 text-emerald-800" : "bg-slate-200 text-slate-700"}`}>{site === "new" ? "New site" : "Old site"}</span>;

function LinkOldDialog({ item, onClose }) {
  const [rows, setRows] = useState((item.linked || []).map((f) => ({ ...f })));
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/new-catalogue/subscriptions/${item.legacy_id}/filters`, { confirm: true, filters: rows.map((r) => ({ product_legacy_id: r.product_legacy_id, qty: Number(r.qty) || 1 })) });
      toast.success("Filters linked"); onClose(true);
    } catch (e) { toast.error(e.response?.data?.detail || "Could not save"); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose(false)}>
      <DialogContent className="w-[95vw] max-w-xl" data-testid="due-link-dialog">
        <DialogHeader><DialogTitle>Link filters</DialogTitle></DialogHeader>
        <p className="text-sm font-semibold text-slate-700">{item.name}</p>
        <p className="text-xs text-slate-500">Filters sent per delivery for this subscription — applies to all its payment plans (monthly, 6-monthly, yearly). Only TedOps is changed.</p>
        {rows.map((r, i) => (
          <div key={r.product_legacy_id} className="flex items-center gap-2 text-sm">
            <span className="flex-1">{r.title}</span>
            <input type="number" min="1" className="h-8 w-16 rounded border border-slate-200 px-2" value={r.qty} onChange={(e) => setRows((x) => x.map((y, j) => (j === i ? { ...y, qty: e.target.value } : y)))} data-testid={`due-link-qty-${r.product_legacy_id}`} />
            <button type="button" className="text-xs text-rose-600" onClick={() => setRows((x) => x.filter((_, j) => j !== i))}>Remove</button>
          </div>))}
        <AddComponent taken={rows.map((r) => r.product_legacy_id)} onAdd={(p) => setRows((x) => [...x, { product_legacy_id: p.legacy_id, title: p.title, qty: 1 }])} />
        <Button disabled={busy} onClick={save} data-testid="due-link-save">{busy ? "Saving…" : "Save filters"}</Button>
      </DialogContent>
    </Dialog>
  );
}

function Packing({ data }) {
  const label = new Date(`${data.month}-01T00:00:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  return (
    <div className="ted-card p-5" data-testid="due-packing">
      <h2 className="mb-2 flex items-center gap-2 text-base font-bold"><Package className="w-4 h-4 text-indigo-500" /> To pack — {label}</h2>
      {data.packing.length ? data.packing.map((p) => (
        <div key={p.product_legacy_id} className="flex justify-between border-t border-slate-100 py-1.5 text-sm" data-testid={`due-pack-${p.product_legacy_id}`}>
          <Link to={`/products/${p.product_legacy_id}`} className="text-indigo-700 hover:underline">{p.title}</Link><b>× {p.qty}</b>
        </div>)) : <p className="text-sm text-slate-500">Nothing linked to pack yet.</p>}
    </div>
  );
}

export default function SubscriptionsDuePage() {
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [linking, setLinking] = useState(null);
  const load = useCallback(() => {
    setData(null);
    api.get("/new-catalogue/subscriptions/due", { params: { month } }).then((r) => { if (r.data.month === month) { setData(r.data); } }).catch((e) => toast.error(e.response?.data?.detail || "Could not load"));
  }, [month]);
  useEffect(() => { load(); }, [load]);
  const refresh = async () => {
    setBusy(true);
    try { const r = await api.post("/new-catalogue/subscriptions/customers/refresh", { confirm: true }, { timeout: 180000 }); toast.success(`Read ${r.data.old} old-site and ${r.data.new} new-site subscriptions`); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Could not read the websites"); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-5 p-4 md:p-6" data-testid="subscriptions-due-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 text-2xl font-extrabold text-slate-900"><CalendarClock className="w-6 h-6 text-indigo-500" /> Subscription deliveries</h1>
          <p className="text-xs text-slate-500">Active subscriptions from both websites. Monthly: filters at 6 & 12 months · 6-monthly: each payment · Yearly: the yearly allocation each payment.{data?.synced_at && ` Last read ${new Date(data.synced_at).toLocaleString("en-GB")}.`}</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <input type="month" value={month} onChange={(e) => setMonth(e.target.value)} className="h-9 rounded-md border border-slate-200 px-2 text-sm" data-testid="due-month-input" />
          <Button variant="outline" disabled={busy} onClick={refresh} data-testid="due-refresh-btn"><RefreshCw className={`w-4 h-4 ${busy ? "animate-spin" : ""}`} /> {busy ? "Reading…" : "Read from websites"}</Button>
        </div>
      </div>
      {!data ? <p className="text-sm text-slate-500">Loading…</p> : (
        <div className="grid gap-5 lg:grid-cols-3">
          <div className="space-y-5 lg:col-span-1">
            <Packing data={data} />
            {data.unlinked.length > 0 && (
              <div className="ted-card p-5" data-testid="due-unlinked">
                <h2 className="mb-2 text-base font-bold text-amber-800">Needs filters linking ({data.unlinked.length})</h2>
                {data.unlinked.map((u) => (
                  <div key={`${u.site}-${u.product_id}`} className="flex items-center justify-between gap-2 border-t border-slate-100 py-1.5 text-xs">
                    <span><SiteBadge site={u.site} /> {u.name}</span>
                    {u.legacy_id ? <Button size="sm" variant="outline" onClick={() => setLinking(u)} data-testid={`due-link-btn-${u.product_id}`}>Link</Button>
                      : <span className="text-[11px] text-amber-700">Import subscriptions first</span>}
                  </div>))}
              </div>)}
          </div>
          <div className="ted-card overflow-x-auto p-0 lg:col-span-2">
            <table className="w-full text-sm" data-testid="due-table">
              <thead className="bg-slate-50 text-left text-xs text-slate-500"><tr><th className="p-3">Due</th><th className="p-3">Customer</th><th className="p-3">Subscription</th><th className="p-3">Filters</th></tr></thead>
              <tbody>
                {data.rows.length === 0 && <tr><td colSpan={4} className="p-6 text-center text-slate-500" data-testid="due-empty">No deliveries due this month{data.synced_at ? "" : " — click Read from websites first"}.</td></tr>}
                {data.rows.map((r) => (
                  <tr key={`${r.site}-${r.wc_sub_id}-${r.product_id}-${r.due}`} className="border-t border-slate-100 align-top" data-testid={`due-row-${r.site}-${r.wc_sub_id}-${r.product_id}`}>
                    <td className="whitespace-nowrap p-3">{new Date(r.due).toLocaleDateString("en-GB")}{r.full_year && <div className="text-[10px] font-bold text-violet-700">FULL YEAR</div>}</td>
                    <td className="p-3"><div className="font-semibold">{r.customer || r.email}</div><div className="text-xs text-slate-500">{r.postcode} · #{r.wc_sub_id} <SiteBadge site={r.site} /></div></td>
                    <td className="p-3 text-xs">{r.product}<div className="text-slate-500">{r.plan === "month/1" ? "Monthly" : r.plan === "month/6" ? "Every 6 months" : r.plan === "year/1" ? "Yearly" : r.plan}</div></td>
                    <td className="p-3 text-xs">{r.filters.length ? r.filters.map((f) => <div key={f.product_legacy_id}>{f.qty} × {f.title}</div>) : <span className="text-amber-700">Not linked</span>}
                      {r.legacy_id && r.filters.length > 0 && <button type="button" className="mt-1 text-[11px] text-indigo-700 hover:underline" onClick={() => setLinking({ site: r.site, product_id: r.product_id, legacy_id: r.legacy_id, name: r.product, linked: r.linked })} data-testid={`due-edit-link-${r.wc_sub_id}-${r.product_id}`}>Edit filters</button>}</td>
                  </tr>))}
              </tbody>
            </table>
          </div>
        </div>)}
      {linking && <LinkOldDialog item={linking} onClose={(saved) => { setLinking(null); if (saved) { load(); } }} />}
    </div>
  );
}
